import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Award, Check, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getRoutePath } from '../../../utils/routingUtils';
import { aiImageFeatureForPackage } from '../../../utils/packageAiImagesFeature';
import PackagePlanFooter from '../../billing/PackagePlanFooter';

const StandardView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userPackage = user?.result?.package || 1; // Default to 1 (free)
  const isCurrentPackage = userPackage === 3; // Standard = 3

  const features = [
    { included: true, text: t('packages.standard.feature1', 'Neograničeno poslova') },
    { included: true, text: t('packages.standard.feature2', 'Loyalty program') },
    { included: true, text: t('packages.standard.feature3', 'Osnovni dashboard') },
    { included: true, text: t('packages.standard.feature4', 'Upravljanje poslovima') },
    { included: true, text: t('packages.standard.feature5', 'Upravljanje klijentima') },
    aiImageFeatureForPackage(t, 'standard'),
    {
      included: true,
      text: t('home.landing.pricing.standard.features.inventory', 'Sekcija Roba'),
    },
    {
      included: true,
      text: t('home.landing.pricing.standard.features.warehouseParts', 'Magacin - rezervni delovi'),
    },
    {
      included: true,
      text: t('home.landing.pricing.standard.features.reversiWithdrawn', 'Reversi - povučena roba'),
    },
    {
      included: true,
      text: t('home.landing.pricing.standard.features.workOrderPersonalization', 'Personalizacija radnog naloga'),
    },
    {
      included: true,
      text: t('home.landing.pricing.standard.features.vehicles', 'Službena vozila'),
    },
    { included: true, text: t('packages.featureVoiceInput', 'Glasovni unos podataka') },
    { included: false, text: t('packages.standard.feature6', 'Web Shop') },
    { included: false, text: t('packages.standard.feature7', 'AI optimizacija') }
  ];

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-blue-700">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-blue-500/20 rounded-lg p-4">
              <Award className="h-8 w-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{t('packages.standard.title', 'Standard Paket')}</h1>
              <p className="text-gray-400 mt-1">{t('packages.standard.subtitle', 'Marketing i neograničeno poslova')}</p>
            </div>
          </div>
          
          {isCurrentPackage && (
            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2 text-green-400">
                <Check className="h-5 w-5" />
                <span className="font-medium">{t('packages.currentPackage', 'Trenutni paket')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">{t('packages.features', 'Karakteristike')}</h2>
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className={feature.detail ? 'space-y-1.5' : ''}>
                <div className="flex items-center gap-3">
                  {feature.included ? (
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
                  ) : (
                    <X className="h-5 w-5 text-gray-600 flex-shrink-0" />
                  )}
                  <span className={feature.included ? 'text-white' : 'text-gray-500 line-through'}>
                    {feature.text}
                  </span>
                </div>
                {feature.detail && (
                  <p className="text-sm text-amber-200/90 leading-snug pl-8 border-l-2 border-amber-500/35 ml-1">
                    {feature.detail}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <PackagePlanFooter packageId={3} theme="blue" />

        {!isCurrentPackage && (
          <div className="bg-blue-900/20 border border-blue-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-3">{t('packages.upgradeHigherTitle', 'Nadogradi na viši paket')}</h3>
            <p className="text-gray-300 mb-4">{t('packages.upgradeToBusinessHint', 'Pogledajte Business paket za Web Shop i dodatne mogućnosti.')}</p>
            <button
              onClick={() => navigate(getRoutePath('packages/business'))}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              {t('packages.upgradeToBusiness', 'Business')}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardView;
